import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { OnDestroy } from '@angular/core';
import { YouTubePlayer } from '@angular/youtube-player';
import { ICommonVideoPlayer } from '@epicuro-next/ataraxia/models';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';

const SAFE_BUFFERING_TIME_DELAY = 350;

@Component({
  selector: 'atx-youtube-player',
  templateUrl: './youtube-player.component.html',
  styleUrls: ['./youtube-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class YoutubePlayerComponent implements ICommonVideoPlayer, OnDestroy {
  public pipActive = false;
  public playerAutoplay = 0;
  // tslint:disable-next-line:variable-name
  private _autoplay = false;

  private idMatcher =
    /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|live\/|shorts\/)([^#\&\?]*).*/gi;

  @ViewChild(YouTubePlayer)
  public player: YouTubePlayer;

  @Input()
  public url: string;

  @Input()
  public id: string;

  @Input()
  public set autoplay(value: boolean) {
    this.playerAutoplay = value ? 1 : 0;
    this._autoplay = value;
  }

  @Input()
  public muted = true;

  @Input()
  public loop = true;

  @Input()
  public set controls(value: boolean) {
    this.controlsEnabled = Number(value);
  }

  @Input()
  public disablePlaceholder = true;

  @HostBinding('class.isDisabled')
  @Input()
  public disabled = false;

  public controlsEnabled = 1;

  private playCommand$$ = new BehaviorSubject(this._autoplay);

  @Input()
  public set play(play: boolean) {
    this.playCommand$$.next(play ?? this._autoplay);
  }

  @Output()
  public videoReady: EventEmitter<void> = new EventEmitter();

  @Output()
  public videoError = new EventEmitter();

  private destroy$$ = new Subject<void>();

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public get videoId() {
    if (this.id) {
      return this.id;
    }

    const match = this.url.split(this.idMatcher);
    if (match && match.length > 1) {
      return match[1];
    }
  }

  public playerReady() {
    this.videoReady.emit();
    if (this.muted) {
      this.player.mute();
    }

    this.playCommand$$
      .pipe(
        debounceTime(SAFE_BUFFERING_TIME_DELAY),
        startWith(this._autoplay),
        takeUntil(this.destroy$$),
      )
      .subscribe((value) => this.videoController(value));
  }

  public requestPictureInPicture() {
    this.pipActive = true;
  }

  public exitPictureInPicture() {
    this.pipActive = false;
  }

  // to avoid pausing video when switching between different videos inside one player
  public onVideoStateChangeEvent = (event: YT.OnStateChangeEvent) => {
    if (event.data === YT.PlayerState.CUED) {
      this.player.playVideo();
    }
    if (event.data === YT.PlayerState.ENDED && this.loop) {
      this.player.playVideo();
    }
  };

  private videoController(play: boolean) {
    if (!this.player) {
      return;
    }

    if (play) {
      this.player.playVideo();
      return;
    }

    this.player.pauseVideo();
  }
}
